import { SOURCE_AUDIT_LABEL, eventAudit, nativeFileRequests, recipeAudit, systemAudit } from './sourceAudit'

/**
 * Respuestas de la AUDITORÍA TÉCNICA del paquete del 08/10/2026 (auditorías del staff del 08/10 y del 11/10/2026).
 *
 * - Procedencia propia: NO son confirmaciones de administración (adminConfirmations) ni pruebas en el juego (inGameTests).
 * - Comprueban la copia local de los 47 CSV (filas, bytes, SHA256 y columnas = 00_control.csv); no prueban
 *   la configuración desplegada hoy ni el comportamiento dentro del juego.
 * - Los CSV siguen siendo la fuente de datos: aquí solo se explica cómo leerlos y qué falta realmente.
 *
 * Efecto en «Pendientes de la guía», por clave original:
 * - resolved: deja de ser pregunta; pasa a «Resuelto con la auditoría técnica» (con el error corregido).
 * - narrowed: REEMPLAZA «Se sabe», «Falta» y la pregunta, para que los tres coincidan.
 */
export const TECHNICAL_AUDIT_LABEL = 'Auditoría técnica del paquete · 08/10/2026'

type Ev = 'administración' | 'captura' | 'configuración' | 'prueba en el juego'

export type TechnicalAnswer =
  | { kind: 'resolved'; error: string; summary: string; label?: string }
  | { kind: 'narrowed'; known: string; missing: string; question: string; evidence?: Ev[]; /** Conserva además el detalle calculado (p. ej. la lista de mapas). */ appendComputed?: boolean; label?: string }

/** Exportación nativa de NPC pendiente (solicitud NPC_ORIGEN). */
export const NPC_ORIGIN_KEY = 'source:npc-origen'
/** Tiendas y servicios (solicitud TIENDAS_SERVICIOS). */
export const SHOPS_SOURCE_KEY = 'source:tiendas-servicios'
/** Una sola solicitud de archivos para los sistemas (cada sistema conserva su propia verificación). */
export const SYSTEMS_SOURCE_KEY = 'source:sistemas'
/** Una sola solicitud técnica: configuración/código del servidor (mezclas, efectos de misiones, combo, posiciones en eventos). */
export const SERVER_SOURCE_KEY = 'source:servidor'
/** Pregunta única COORDENADAS: posiciones, imágenes y calibración con estados separados. */
export const COORD_KEY = 'coordenadas'

const A11 = SOURCE_AUDIT_LABEL
const join = (xs: string[]) => xs.join(' · ')

const recipeAnswers: Record<string, TechnicalAnswer> = Object.fromEntries(
  Object.entries(recipeAudit).map(([k, r]) => [
    `recipe:${k}`,
    {
      kind: 'narrowed',
      label: A11,
      known: `Ítems y materiales identificados: ${r.known.join('; ')}.${r.note ? ` ${r.note}` : ''} La receta clásica puede seguir visible como referencia, sin verde de configuración.`,
      missing: `Ficha de fabricación completa: ${join(r.missing)}`,
      question: `Se resuelve con ${SERVER_SOURCE_KEY} (ChaosMix / ChaosBox o tabla oficial). No se pregunta a administración receta por receta.`,
      evidence: ['configuración'],
    } as TechnicalAnswer,
  ]),
)

const systemAnswers: Record<string, TechnicalAnswer> = Object.fromEntries(
  Object.entries(systemAudit).map(([k, s]) => [`system:${k}`, { kind: 'narrowed', label: A11, known: s.known, missing: s.missing, question: s.question, evidence: ['configuración'] } as TechnicalAnswer]),
)

const eventAnswers: Record<string, TechnicalAnswer> = Object.fromEntries(
  Object.entries(eventAudit).map(([id, e]) => [
    id.startsWith('invasion:') ? `event-manual:${id}` : `event-trigger:${id}`,
    {
      kind: 'narrowed',
      label: A11,
      known: `${e.known.join('; ')}.${e.note ? ` ${e.note}` : ''}`,
      missing: join(e.missing),
      question: id.startsWith('invasion:') ? '¿El staff lo activa manualmente o cambió su estado? Si no, se mantiene «Inactivo en la configuración del 08/10/2026».' : `Leer ${e.sources.find((s) => s.startsWith('Fuente nativa')) ?? 'el archivo nativo del evento'} y su implementación (${SERVER_SOURCE_KEY}).`,
      evidence: id.startsWith('invasion:') ? ['administración'] : ['configuración'],
    } as TechnicalAnswer,
  ]),
)

export const technicalAnswers: Record<string, TechnicalAnswer> = {
  'invasion:golden': {
    kind: 'resolved',
    error: 'La guía contaba como conflicto los posibles premios de las cajas Kundun, como si el dorado los soltara directamente.',
    summary:
      'Los 20 recorridos directos entregan Box of Kundun 14,11@8…@12; las 382 rutas restantes son posibles premios al abrir esas cajas. Se presenta «Monstruo dorado → Box of Kundun → posible premio».',
  },
  // Reincorporado (11/10/2026): mientras sus recompensas sigan retenidas, queda pendiente SOLO la vigencia.
  'invasion:white-wizard': {
    kind: 'narrowed',
    label: A11,
    known:
      "Configuración: tres joyas y una tirada del 20 % de un equipo adicional Excellent; sus variantes son +2 a +5 con perfil de una opción Excellent (bolsa 27 → variante 377 → perfil 4:12). Ese 100 % se aplica al equipo elegido, no a todas las muertes ni a las joyas. Acompañantes: 20 % Wizard's Ring y 80 % 75.000 Zen. Cantidades aprobadas y configuración coinciden. Corrección previa: quality vacío ya no se trata como «normal».",
    missing: 'Solo la vigencia actual o los cambios concretos',
    question: '¿La recompensa del White Wizard exportada el 08/10/2026 sigue vigente? Si cambió, ¿qué fila o ítem? (Se puede comprobar con 027 White Wizard.txt y 137/138.)',
    evidence: ['administración', 'configuración'],
  },
  'invasion:red-dragon': {
    kind: 'narrowed',
    label: A11,
    known:
      "Bag 2: grupos 0/1 entregan dos joyas y grupos 3/4 dos equipos NO Excellent +9/+10 — coincide con lo aprobado (tres dragones: Lorencia, Devias y Noria). Diferencia 1: el pool 20 tiene 139 equipos e incluye Dragon Helm 7,1 (b:2:pool:20:row:101, variante 5), distinto de la instrucción del staff. Diferencia 2: el grupo 6 tiene una tirada de 20/10.000 (0,2 %) del pool 22 — Loch's Feather 13,14@0 o Crest of Monarch 13,14@1, reparto interno 50/50 — que no estaba en el anuncio aprobado; es una tirada de la bolsa, no una probabilidad final por muerte, y no se publica como premio vigente. El drop normal de Dragon Helm en otros monstruos no cambia.",
    missing: 'Archivo vigente (002 - Red Dragon.txt) o la corrección exacta de Dragon Helm en el pool 20 y de la tirada del grupo 6',
    question: '¿La bag 2 desplegada hoy incluye Dragon Helm (7,1) y la tirada del grupo 6? Enviar 002 - Red Dragon.txt o indicar la fila exacta que cambió.',
    evidence: ['administración', 'configuración'],
  },
  'invasion:death-king': {
    kind: 'narrowed',
    label: A11,
    known:
      'Coincide con lo aprobado: un jefe y diez acompañantes en Lorencia o Noria (mapas alternativos, no se suman); el jefe tiene dos tiradas garantizadas de joya y cada Death Bone un equipo NO Excellent +6 a +9. Joyas: Chaos 12,15 · Bless 14,13 · Soul 14,14 · Life 14,16 · Creation 14,22, con los pesos de cada bolsa. La lista de equipos ya está en las bolsas; «tier 1–4» no sustituye esa lista.',
    missing: 'Solo la vigencia o cambios concretos',
    question: '¿Sigue desplegada esta recompensa? Se puede comprobar con 001 - Skeleton King.txt y 139 - Death Bone.txt.',
    evidence: ['administración', 'configuración'],
  },
  'map-access:none': {
    kind: 'narrowed',
    label: A11,
    known:
      '33_event_rooms: 28 reglas de salas para 23 mapas, con sus niveles (se conservan aunque falte el acceso). Un mapa sin /move no es inaccesible ni de nivel 1: Barracks y Refuge tienen un valor técnico 1 que no se publica como requisito. Devil Square y Chaos Castle están habilitados en main1/main2; Devil Square 7 deshabilitada; Chaos Castle 6 marcada con rango inválido 851–100 y active = false (no se corrige adivinando 1000 ni se desactiva el evento).',
    missing: 'Mecanismo real de entrada (portal, NPC, ítem o misión) y sus condiciones, y el rango vigente de Chaos Castle 6',
    question: 'Enviar la configuración de transporte de Barracks/Refuge (con los Event/*.dat) e indicar el rango vigente de Chaos Castle 6.',
    evidence: ['administración', 'configuración'],
    appendComputed: true,
  },
  'combo:bk': {
    kind: 'narrowed',
    label: A11,
    known: 'Misión 3: nivel 220, Marlon 229, misión 2 terminada, 20.000 Zen y 1 unidad de 14,24@1. quest-rule-015 solo habilita a DK con required_change_up_plus_one = 2 (mínimo, no igualdad). No demuestra la secuencia ni oculta el combo a Blade Master.',
    missing: 'IDs y orden de las habilidades válidas · ventana de tiempo · restricciones de ejecución · persistencia al evolucionar',
    question: `Se resuelve con ${SERVER_SOURCE_KEY}: la función real que valida el combo (nombre por identificar con el desarrollador).`,
    evidence: ['configuración'],
  },
  'quest-reward:sebina': {
    kind: 'narrowed',
    label: A11,
    known: 'Misiones 0 y 1: nivel 150, NPC 235; la 1 exige la 0 terminada. Misión 0: 10.000 Zen y 1 × 14,23@0. Misión 1: 20.000 Zen y un material según clase (40_quest_objective_classes; no se suman las cuatro alternativas).',
    missing: 'Cambio de clase al completar cada etapa, por clase de origen · puntos, otros premios y efectos',
    question: `Se resuelve con ${SERVER_SOURCE_KEY} (Quest.txt y su implementación). No se vuelven a pedir requisitos ni objetivos.`,
    evidence: ['configuración'],
  },
  'quest-reward:marlon': {
    kind: 'narrowed',
    label: A11,
    known: 'Misión 2: NPC 229, nivel 220, misión 1 terminada, 30.000 Zen, objetivo 1 × 14,23@1. El nivel 220 es requisito de aceptación, no el nivel desde el que se aplica un beneficio.',
    missing: 'Beneficio al completar · puntos adicionales por nivel, retroactividad y nivel de aplicación',
    question: `Se resuelve con ${SERVER_SOURCE_KEY} (Quest.txt y su implementación).`,
    evidence: ['configuración'],
  },
  'quest-reward:devin': {
    kind: 'narrowed',
    label: A11,
    known: 'Misión 4: nivel 380, 50.000 Zen, 1 × 14,65@0, 14,66@0 y 14,67@0 (requisito previo por clase en reglas 016–018). Misión 5: nivel 400, misión 4 terminada, 70.000 Zen, 20 bajas de 409, 410 y 411. Misión 6: nivel 400, misión 5 terminada, 100.000 Zen, 1 baja de 412.',
    missing: 'Efectos y recompensas por etapa · cambio de clase por clase de origen',
    question: `Se resuelve con ${SERVER_SOURCE_KEY} (Quest.txt y su implementación).`,
    evidence: ['configuración'],
  },
  'quest-unpublished:7-9': {
    kind: 'narrowed',
    label: A11,
    known: '12 reglas usan el NPC 766 y 5 objetivos los monstruos 763, 767, 770 y 771, ausentes de 06_monsters. Misión 7: 1 baja de 763; misión 8: 5 bajas de 767, 770 y 771; misión 9: 1 baja de 763. La misión 7 pide 380 en estado normal y 400 en otros. Los títulos de 36_quests son generados, no oficiales. Se mantienen solo en diagnóstico.',
    missing: 'Habilitación y alcance Season 6 · identidad y apariciones del NPC y los monstruos · títulos, propósito y efectos · mínimo 380/400 de la misión 7',
    question: '¿Se usan las misiones 7–9? Si sí: nombres, efectos y catálogo de 766/763/767/770/771. Si no: confirmar que siguen fuera de la guía pública.',
    evidence: ['administración'],
  },
  'items-no-routes': {
    kind: 'narrowed',
    label: A11,
    known: 'Alcances separados: sin ruta en los CSV brutos (publicados + excluidos) frente a sin ruta entre los publicados; los excluidos se cuentan aparte en el detalle. Ejemplos con identidad conocida: Bolt 4,7, Arrow 4,15, Dinorant 13,3, Dark Horse 13,4, Dark Raven 13,5. Sin ruta de drop no significa no disponible. Familias como Lucky o Manticore se concilian con las exclusiones del staff con evidencia, no por nombre ni por tener imagen.',
    missing: 'Solo el residuo tras cruzar tiendas, fabricación y otras fuentes oficiales',
    question: `Primero se cruzan ${SHOPS_SOURCE_KEY} y ${SERVER_SOURCE_KEY}; después se pregunta solo por los ítems que queden sin vía. No se piden respuestas ítem por ítem.`,
    evidence: ['configuración'],
    appendComputed: true,
  },
  [NPC_ORIGIN_KEY]: {
    kind: 'narrowed',
    label: A11,
    known:
      'El exportador _mapas.py descarta los NPC en location() y add_spawn(), por eso no hay coordenadas de NPC en 08, 09, 31 ni 32; no demuestra que falten en el servidor. Pet Trainer 234 está unificado con Trainer 226 (administración, 11/10/2026) y no se cuenta aparte. Robot 805: un NPC con cuatro instancias confirmadas por administración (11/10/2026) — Lorencia 131,122 · Devias 196,50 · Noria 107,109 · Elbeland 38,216 —; función de venta confirmada, inventario y precios no.',
    missing: 'Apariciones nativas de los demás NPC y, si no está ahí, la rotación de Marlon',
    question: `Enviar ${nativeFileRequests.npc} No basta con quitar el filtro del exportador para mezclarlos con spots de monstruos.`,
    evidence: ['configuración'],
    appendComputed: true,
  },
  [SHOPS_SOURCE_KEY]: {
    kind: 'narrowed',
    label: A11,
    known: 'Ni los 47 CSV ni el SQLite del ZIP contienen inventarios, precios, monedas ni la relación NPC–tienda. Funciones documentadas: NPC de misiones (37_quest_rules), Trainer 226 como fabricante canónico aprobado, Robot como vendedor (administración). El Zen de una bolsa no es precio de tienda; una función de venta no es su stock.',
    missing: 'Archivos vigentes de tiendas y asignación por NPC o instancia, con item_id, variante, cantidad, precio y moneda (o su regla)',
    question: `Enviar ${nativeFileRequests.shops} Si no se localizan, una captura o listado de las carpetas de configuración de tiendas.`,
    evidence: ['configuración'],
  },
  [SERVER_SOURCE_KEY]: {
    kind: 'narrowed',
    label: A11,
    known: 'Faltan efectos de misiones, recetas completas y posiciones de los eventos; sí hay requisitos y objetivos de misiones 0–6, identidades de materiales, rutas alternativas y perfiles parciales. 15_route_ingredients son relaciones con intermediarios, no recetas con cantidades. Conversiones parciales ya documentadas: Sealed Golden Box + Gold Key y Sealed Silver Box + Silver Key.',
    missing: 'Recetas completas (bloque común) · efectos de misiones · validación del combo · posiciones de eventos por fase y subservidor',
    question: `Enviar ${nativeFileRequests.server}`,
    evidence: ['configuración'],
    appendComputed: true,
  },
  [SYSTEMS_SOURCE_KEY]: {
    kind: 'narrowed',
    label: A11,
    known: 'Cada sistema conserva su propia verificación; los archivos se piden juntos una sola vez.',
    missing: 'Configuración de clases, habilidades, comandos, bonus, Socket/380, Helper, Master, resets, Harmony, Market, VIP y Pase',
    question: `Enviar ${nativeFileRequests.systems}`,
    evidence: ['configuración'],
  },
  [COORD_KEY]: {
    kind: 'narrowed',
    label: A11,
    known: '08_spawns trae coordenadas de spots y 32_event_spawn_rules reglas de invasión; 09_monster_locations documenta relaciones, no X/Y. Una X/Y documentada se publica aunque el marcador no esté calibrado. 47_map_images: 58 imágenes, todas con horizontal Y y vertical X (se leen por mapa); los demás mapas del catálogo no tienen PNG y solo se piden los que se publiquen. Siguen aparte: pares de misión sin aparición y los spots 039-spot-normal-47/48 de Kanturu 3 descartados por no_spawnable_terrain_tile.',
    missing: 'Origen, sentido y escala de cada imagen, con 2–3 referencias visibles por mapa',
    question: 'Capturas con 2–3 puntos y sus coordenadas por mapa publicado. Una captura no valida los demás mapas.',
    evidence: ['captura', 'prueba en el juego'],
    appendComputed: true,
  },
  'control:VIGENCIA': {
    kind: 'narrowed',
    label: A11,
    known: 'metadata.json declara copia local del 08/10/2026 con runtime_verified = false. Los 47 CSV coinciden con 00_control.csv (filas, bytes, SHA256, columnas): eso prueba integridad de la copia, no que sea la desplegada.',
    missing: 'Versión vigente por servermain1, servermain2 y servercastle, con fecha y diferencias',
    question: 'Regenerar la exportación desde lo desplegado, comparar los hashes de las fuentes, o confirmar expresamente con sus límites.',
    evidence: ['administración', 'configuración'],
  },
  ...recipeAnswers,
  ...systemAnswers,
  ...eventAnswers,
}

/** Respuesta por clave exacta; las posiciones de eventos comparten una respuesta común. */
export const technicalAnswer = (key: string): TechnicalAnswer | undefined => {
  if (technicalAnswers[key]) return technicalAnswers[key]
  if (key.startsWith('event-positions:')) {
    return {
      kind: 'narrowed',
      label: A11,
      known: '31_event_monsters documenta monstruo, mapa, fase y actividad; 32_event_spawn_rules solo trae reglas de invasiones. Los conteos son registros, no enemigos simultáneos; las filas inactivas se mantienen en diagnóstico, aparte de las ubicaciones jugables. El mapa conocido se conserva aunque falten X/Y.',
      missing: 'Solo el punto, área o regla dinámica de aparición por fase y subservidor',
      question: `Exportar desde el archivo nativo del evento (${SERVER_SOURCE_KEY}); no se pide a administración escribirlas a mano.`,
      evidence: ['configuración'],
      appendComputed: true,
    }
  }
  return undefined
}

/** Texto público corto para fichas de NPC sin ubicación (sin publicar el informe técnico completo). */
export const NPC_LOCATION_GAP =
  'La exportación del 08/10/2026 no incluye ubicaciones de NPC. Falta: mapa y coordenadas X/Y.'
export const SHOP_INVENTORY_GAP = 'Inventario de tienda pendiente de documentar.'
